import { defineComponent, ref, computed, h } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { container, loader, loadingButton, pageTitle } from '@/ui/components';
import { useTask } from '@/ui/concerns';
import { create } from '@/services/api/resources/entityItem';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as fetchAllEntityTypes } from '@/services/api/resources/entityType';
import { filterListToOptions, sequence, goBack } from '@/ui/helpers/uiHelper';
import { hasFeature, hasPermission, Permissions, user } from '@/ui/auth/auth';
import { SelectOptions } from '@/ui/types';
import { useEntityItemRecord } from './useEntityItemRecord';
import { useEntityItemForm } from './useEntityItemForm';
import { queryTabName, EntityItemsTabNames } from '@/ui/components/navigation/constants';
import { useCreatableUserModal } from '@/ui/pages/useCreatableUserModal';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { useEntityTypePropertiesLoader } from './useEntityTypePropertiesLoader';
import { useCurrentRoute, router } from '@/ui/routes';
import { Icon } from '@/ui/components/icon/Icon';
import { toasted } from '@/ui/helpers';

export const EntityItemCreatePage = defineComponent({
  name: 'EntityItemCreatePage',
  setup() {
    const route = useCurrentRoute();
    if (!hasFeature('entityItems')) {
      return renderResourceTeaserPage(t('common.entity-items'));
    }

    if (!hasPermission(Permissions.MANAGE_ENTITY_ITEMS)) {
      return renderForbiddenPage;
    }

    const userOptions = ref<SelectOptions>([]);
    const entityTypeOptions = ref<SelectOptions>([]);

    const record = useEntityItemRecord();

    const getData = useTask(
      () => sequence(getAllUsers(), fetchAllEntityTypes()),
      ([users, entityTypes]) => {
        userOptions.value = filterListToOptions(users);
        entityTypeOptions.value = filterListToOptions(entityTypes);

        const entityTypeQuery = route.value.query.entityType;
        if (entityTypeQuery && typeof entityTypeQuery === 'string') {
          const entityTypeId = parseInt(entityTypeQuery, 10);
          const isValidEntityType = !Number.isNaN(entityTypeId)
            && entityTypeOptions.value.some(opt => opt.value === entityTypeId);

          if (isValidEntityType) {
            record.value.entityTypeId = entityTypeId;
          }
        }
      },
    );

    const sendRecord = useTask(
      () => create(record.value),
      () => {
        toasted.success(t('entity-item.created'));

        if (route.value.query.entityType) {
          router.push({ name: 'entityTypeItems', params: { id: String(route.value.query.entityType) } });

          return;
        }

        router.push({ name: 'entityItems', query: { [queryTabName]: EntityItemsTabNames.ITEMS } });
      },
    );

    const entityTypeId = computed(() => record.value.entityTypeId);
    const { entityTypeProperties, loadEntityTypeProperties } = useEntityTypePropertiesLoader(entityTypeId);

    const { formFields, handleSubmit } = useEntityItemForm(
      record,
      sendRecord,
      loadEntityTypeProperties,
      user.value,
    );

    useCreatableUserModal(userOptions);

    return function render() {
      return container(
        [
          h('div', { class: 'level level--page-header' }, [
            h('div', { class: 'level-left' }, [
              h(Icon, {
                props: { icon: 'arrow-left' },
                class: 'entity-item-page__back-icon',
                nativeOn: { click: goBack },
              }),
              pageTitle(t('entity-item.create')),
            ]),
          ]),
          loader('form', getData, [
            ...formFields(userOptions.value, entityTypeOptions.value, entityTypeProperties.value),
            loadingButton(
              t('entity-item.create'),
              handleSubmit,
              sendRecord.state.value.pending,
            ),
          ]),
        ],
        'narrow',
        'entity-item-page',
      );
    };
  },
});
